/**
 * Extracts institution and organization IDs from a URL hash
 * @param url The URL hash to extract IDs from (e.g. "#/account/institution/123/overview" or "#/account/organization/456/settings")
 * @returns Object containing institutionId and organizationId if found
 */
export interface ExtractedIds {
    institutionId?: string;
    organizationId?: string;
}

export const extractIdsFromUrl = (url?: string): ExtractedIds => {
    if (!url) {
        return {
            institutionId: undefined,
            organizationId: undefined,
        };
    }

    // Clean the URL by removing the hash prefix if present
    const cleanUrl = url.startsWith('#') ? url.substring(1) : url;

    // Extract institution ID using regex:
    // - "institution/" matches the literal text "institution/" in the URL
    // - ([^/?]+) captures one or more characters that are not forward slashes or question marks:
    //   - [^/?] is a negated character set that matches any character except '/' and '?'
    //   - + means "one or more" of these characters
    //   - () creates a capture group that we can reference later
    // Examples:
    // - From "/institution/abc-123/overview" captures "abc-123"
    // - From "/institution/def-456?param=value" captures "def-456"
    const institutionMatch = cleanUrl.match(/institution\/([^/?]+)/);
    const institutionId = institutionMatch ? institutionMatch[1] : undefined;

    // Extract organization ID using the same pattern:
    // - "organization/" matches the literal text "organization/" in the URL
    // - ([^/?]+) captures the ID, stopping at either '/' or '?' characters
    // Examples:
    // - From "/organization/xyz-789/settings" captures "xyz-789"
    // - From "/organization/uvw-012?tab=overview" captures "uvw-012"
    const organizationMatch = cleanUrl.match(/organization\/([^/?]+)/);
    const organizationId = organizationMatch ? organizationMatch[1] : undefined;

    return {
        institutionId,
        organizationId,
    };
};
